import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import styles from './PlanoContas.module.css';

const TIPOS = [
  { value: 'ativo',      label: 'Ativo' },
  { value: 'passivo',    label: 'Passivo' },
  { value: 'patrimonio', label: 'Patrimônio Líquido' },
  { value: 'receita',    label: 'Receita' },
  { value: 'custo',      label: 'Custo' },
  { value: 'despesa',    label: 'Despesa' },
  { value: 'outro',      label: 'Outros' },
];

const TIPO_LABEL = Object.fromEntries(TIPOS.map(t => [t.value, t.label]));

// Build parent→children map from flat list
function buildTree(rows) {
  const map = {};
  rows.forEach(r => { map[r.id] = { ...r, children: [] }; });
  const roots = [];
  rows.forEach(r => {
    if (r.pai_id && map[r.pai_id]) {
      map[r.pai_id].children.push(map[r.id]);
    } else {
      roots.push(map[r.id]);
    }
  });
  return roots;
}

const MODAL_VAZIO = {
  id: null, codigo: '', descricao: '', tipo: 'despesa',
  natureza: 'analitica', pai_id: '', aceita_lancamento: true, ativo: true,
};

export default function PlanoContas() {
  const [contas, setContas]         = useState([]);
  const [collapsed, setCollapsed]   = useState(new Set());
  const [loading, setLoading]       = useState(true);
  const [erro, setErro]             = useState('');
  const [modal, setModal]           = useState(null);
  const [salvando, setSalvando]     = useState(false);
  const [errModal, setErrModal]     = useState('');
  const [busca, setBusca]           = useState('');
  const [confirmDelete, setConfirm] = useState(null);
  const [resetando, setResetando]   = useState(false);

  // Autocomplete for pai_id
  const [paiInput, setPaiInput]     = useState('');
  const [paiOpts, setPaiOpts]       = useState([]);
  const [paiAberto, setPaiAberto]   = useState(false);
  const paiRef = useRef();

  const carregar = useCallback(async () => {
    setLoading(true); setErro('');
    try {
      const { data } = await api.get('/financeiro/plano-contas');
      setContas(data);
      // Start with only level-1 expanded (i.e., collapse level-1 groups by default = show all)
      // Actually start with all groups collapsed except level 1 items so user can see structure
      const toCollapse = new Set(data.filter(c => c.natureza === 'sintetica' && c.nivel >= 2).map(c => c.id));
      setCollapsed(toCollapse);
    } catch { setErro('Erro ao carregar plano de contas.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Autocomplete pai_id — filtra da lista já carregada
  useEffect(() => {
    if (!paiAberto) return;
    const filtro = paiInput.toLowerCase();
    const opts = filtro
      ? contas.filter(c =>
          c.codigo.toLowerCase().includes(filtro) || c.descricao.toLowerCase().includes(filtro)
        ).slice(0, 20)
      : contas.slice(0, 20);
    setPaiOpts(opts);
  }, [paiInput, paiAberto, contas]);

  function abrirNovo(paiSugerido = null) {
    const base = { ...MODAL_VAZIO };
    if (paiSugerido) {
      base.pai_id = paiSugerido.id;
      base.tipo   = paiSugerido.tipo;
      setPaiInput(`${paiSugerido.codigo} — ${paiSugerido.descricao}`);
    } else {
      setPaiInput('');
    }
    setModal(base);
    setErrModal('');
  }

  function abrirEditar(conta) {
    setModal({
      id:                conta.id,
      codigo:            conta.codigo,
      descricao:         conta.descricao,
      tipo:              conta.tipo,
      natureza:          conta.natureza,
      pai_id:            conta.pai_id || '',
      aceita_lancamento: conta.aceita_lancamento,
      ativo:             conta.ativo,
    });
    setPaiInput(conta.pai_id
      ? `${conta.pai_codigo} — ${contas.find(c => c.id === conta.pai_id)?.descricao || ''}`
      : '');
    setErrModal('');
  }

  function toggleCollapse(id) {
    setCollapsed(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function expandirTudo() { setCollapsed(new Set()); }
  function recolherTudo() {
    setCollapsed(new Set(contas.filter(c => c.natureza === 'sintetica').map(c => c.id)));
  }

  async function salvar() {
    setSalvando(true); setErrModal('');
    try {
      const body = {
        codigo:            modal.codigo,
        descricao:         modal.descricao,
        tipo:              modal.tipo,
        natureza:          modal.natureza,
        pai_id:            modal.pai_id || null,
        aceita_lancamento: modal.aceita_lancamento,
        ativo:             modal.ativo,
      };
      if (modal.id) {
        await api.put(`/financeiro/plano-contas/${modal.id}`, body);
      } else {
        await api.post('/financeiro/plano-contas', body);
      }
      setModal(null);
      carregar();
    } catch (err) {
      setErrModal(err.response?.data?.erro || 'Erro ao salvar.');
    } finally { setSalvando(false); }
  }

  async function excluir(id) {
    try {
      await api.delete(`/financeiro/plano-contas/${id}`);
      setConfirm(null);
      carregar();
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao excluir.');
    }
  }

  async function resetarPadrao() {
    if (!window.confirm('Isso irá excluir todo o plano atual e restaurar o padrão. Continuar?')) return;
    setResetando(true);
    try {
      await api.post('/financeiro/plano-contas/resetar-padrao');
      carregar();
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao restaurar.');
    } finally { setResetando(false); }
  }

  // Determine if an account should be visible (all ancestors expanded)
  function isVisible(conta, idMap) {
    let cur = conta;
    while (cur.pai_id) {
      if (collapsed.has(cur.pai_id)) return false;
      cur = idMap[cur.pai_id];
      if (!cur) break;
    }
    return true;
  }

  const idMap = Object.fromEntries(contas.map(c => [c.id, c]));
  const buscaLow = busca.toLowerCase();
  const filtradas = busca
    ? contas.filter(c =>
        c.codigo.toLowerCase().includes(buscaLow) ||
        c.descricao.toLowerCase().includes(buscaLow) ||
        TIPO_LABEL[c.tipo]?.toLowerCase().includes(buscaLow)
      )
    : contas.filter(c => isVisible(c, idMap));

  const temFilhos = id => contas.some(c => c.pai_id === id);

  return (
    <div className={styles.page}>
      {/* Cabeçalho */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.titulo}>Plano de Contas</h1>
          <p className={styles.subtitulo}>Estrutura contábil gerencial da organização</p>
        </div>
        <div className={styles.headerAcoes}>
          <input
            className={styles.busca}
            placeholder="Buscar conta..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
          />
          <button className={styles.btnSecundario} onClick={expandirTudo}>Expandir</button>
          <button className={styles.btnSecundario} onClick={recolherTudo}>Recolher</button>
          <button className={styles.btnPerigo} onClick={resetarPadrao} disabled={resetando}>
            {resetando ? 'Aguarde...' : 'Restaurar Padrão'}
          </button>
          <button className={styles.btnPrimario} onClick={() => abrirNovo()}>+ Nova Conta</button>
        </div>
      </div>

      {erro && <div className={styles.alerta}>{erro}</div>}

      {loading ? (
        <div className={styles.carregando}>Carregando plano de contas...</div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.thCodigo}>Código</th>
                <th className={styles.thDesc}>Descrição</th>
                <th className={styles.thTipo}>Tipo</th>
                <th className={styles.thNat}>Natureza</th>
                <th className={styles.thLanc} title="Aceita Lançamento">Lanç.</th>
                <th className={styles.thSit}>Sit.</th>
                <th className={styles.thAcoes}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.length === 0 && (
                <tr>
                  <td colSpan={7} className={styles.vazio}>Nenhuma conta encontrada.</td>
                </tr>
              )}
              {filtradas.map(conta => (
                <ContaLinha
                  key={conta.id}
                  conta={conta}
                  collapsed={collapsed}
                  temFilhos={temFilhos(conta.id)}
                  onToggle={() => toggleCollapse(conta.id)}
                  onEditar={() => abrirEditar(conta)}
                  onNovaSub={() => abrirNovo(conta)}
                  onExcluir={() => setConfirm(conta)}
                  buscaAtiva={!!busca}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Edição / Criação */}
      {modal && (
        <div className={styles.overlay} onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>{modal.id ? 'Editar Conta' : 'Nova Conta'}</h2>
              <button className={styles.fechar} onClick={() => setModal(null)}>✕</button>
            </div>

            {errModal && <div className={styles.errModal}>{errModal}</div>}

            <div className={styles.formGrid}>
              <div className={styles.campo}>
                <label>Código *</label>
                <input
                  className={styles.input}
                  placeholder="Ex: 6.1.08"
                  value={modal.codigo}
                  onChange={e => setModal(m => ({ ...m, codigo: e.target.value }))}
                />
              </div>
              <div className={`${styles.campo} ${styles.campoLargo}`}>
                <label>Descrição *</label>
                <input
                  className={styles.input}
                  placeholder="Nome da conta contábil"
                  value={modal.descricao}
                  onChange={e => setModal(m => ({ ...m, descricao: e.target.value }))}
                />
              </div>
              <div className={styles.campo}>
                <label>Tipo *</label>
                <select
                  className={styles.input}
                  value={modal.tipo}
                  onChange={e => setModal(m => ({ ...m, tipo: e.target.value }))}
                >
                  {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className={styles.campo}>
                <label>Natureza *</label>
                <select
                  className={styles.input}
                  value={modal.natureza}
                  onChange={e => setModal(m => ({
                    ...m, natureza: e.target.value,
                    aceita_lancamento: e.target.value === 'analitica',
                  }))}
                >
                  <option value="sintetica">Sintética (grupo)</option>
                  <option value="analitica">Analítica (lançamentos)</option>
                </select>
              </div>
              <div className={`${styles.campo} ${styles.campoLargo}`} ref={paiRef} style={{ position: 'relative' }}>
                <label>Conta Pai</label>
                <input
                  className={styles.input}
                  placeholder="Pesquisar código ou nome..."
                  value={paiInput}
                  onChange={e => { setPaiInput(e.target.value); setPaiAberto(true); if (!e.target.value) setModal(m => ({ ...m, pai_id: '' })); }}
                  onFocus={() => setPaiAberto(true)}
                  onBlur={() => setTimeout(() => setPaiAberto(false), 150)}
                />
                {paiAberto && paiOpts.length > 0 && (
                  <div className={styles.autocomplete}>
                    {modal.pai_id && (
                      <div
                        className={styles.acOpt}
                        onMouseDown={() => { setPaiInput(''); setModal(m => ({ ...m, pai_id: '' })); setPaiAberto(false); }}
                        style={{ color: '#6B7280', fontStyle: 'italic' }}
                      >
                        — Sem conta pai (conta raiz)
                      </div>
                    )}
                    {paiOpts.map(o => (
                      <div
                        key={o.id}
                        className={styles.acOpt}
                        onMouseDown={() => {
                          setModal(m => ({ ...m, pai_id: o.id }));
                          setPaiInput(`${o.codigo} — ${o.descricao}`);
                          setPaiAberto(false);
                        }}
                      >
                        <span className={styles.acCodigo}>{o.codigo}</span>
                        <span className={styles.acDesc}>{o.descricao}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className={styles.campoCheck}>
                <label>
                  <input
                    type="checkbox"
                    checked={!!modal.aceita_lancamento}
                    onChange={e => setModal(m => ({ ...m, aceita_lancamento: e.target.checked }))}
                  />
                  Aceita Lançamentos
                </label>
                {modal.id && (
                  <label style={{ marginLeft: 24 }}>
                    <input
                      type="checkbox"
                      checked={!!modal.ativo}
                      onChange={e => setModal(m => ({ ...m, ativo: e.target.checked }))}
                    />
                    Conta Ativa
                  </label>
                )}
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.btnSecundario} onClick={() => setModal(null)}>Cancelar</button>
              <button className={styles.btnPrimario} onClick={salvar} disabled={salvando}>
                {salvando ? 'Salvando...' : modal.id ? 'Salvar Alterações' : 'Criar Conta'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete */}
      {confirmDelete && (
        <div className={styles.overlay} onClick={e => e.target === e.currentTarget && setConfirm(null)}>
          <div className={styles.modalPequeno}>
            <h3>Excluir conta?</h3>
            <p><strong>{confirmDelete.codigo}</strong> — {confirmDelete.descricao}</p>
            <p className={styles.aviso}>Esta ação não pode ser desfeita.</p>
            <div className={styles.modalFooter}>
              <button className={styles.btnSecundario} onClick={() => setConfirm(null)}>Cancelar</button>
              <button className={styles.btnPerigo} onClick={() => excluir(confirmDelete.id)}>Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ContaLinha({ conta, collapsed, temFilhos, onToggle, onEditar, onNovaSub, onExcluir, buscaAtiva }) {
  const isSin = conta.natureza === 'sintetica';
  const isCollapsed = collapsed.has(conta.id);
  const indent = (conta.nivel - 1) * 20;

  return (
    <tr className={`${styles.linha} ${isSin ? styles.linhaSin : styles.linhaAn} ${!conta.ativo ? styles.inativo : ''}`}>
      {/* Código */}
      <td className={styles.tdCodigo}>
        <span className={styles.codigo}>{conta.codigo}</span>
      </td>

      {/* Descrição com indent */}
      <td className={styles.tdDesc}>
        <div style={{ display: 'flex', alignItems: 'center', paddingLeft: indent }}>
          {temFilhos && !buscaAtiva ? (
            <button className={styles.toggle} onClick={onToggle} title={isCollapsed ? 'Expandir' : 'Recolher'}>
              {isCollapsed ? '▶' : '▼'}
            </button>
          ) : (
            <span className={styles.toggleEspacador} />
          )}
          <span className={isSin ? styles.descSin : styles.descAn}>{conta.descricao}</span>
        </div>
      </td>

      {/* Tipo */}
      <td className={styles.tdTipo}>
        <span className={`${styles.badge} ${styles[`badge_${conta.tipo}`]}`}>
          {TIPO_LABEL[conta.tipo] || conta.tipo}
        </span>
      </td>

      {/* Natureza */}
      <td className={styles.tdNat}>
        <span className={isSin ? styles.tagSin : styles.tagAn}>
          {isSin ? 'Sintética' : 'Analítica'}
        </span>
      </td>

      {/* Aceita lançamento */}
      <td className={styles.tdLanc}>
        {conta.aceita_lancamento
          ? <span className={styles.check}>✓</span>
          : <span className={styles.dash}>—</span>}
      </td>

      {/* Situação */}
      <td className={styles.tdSit}>
        <span className={conta.ativo ? styles.ativo : styles.inatBadge}>
          {conta.ativo ? 'Ativo' : 'Inativo'}
        </span>
      </td>

      {/* Ações */}
      <td className={styles.tdAcoes}>
        {isSin && (
          <button className={styles.btnAcao} onClick={onNovaSub} title="Nova subconta">+Sub</button>
        )}
        <button className={styles.btnAcao} onClick={onEditar} title="Editar">✏️</button>
        <button className={`${styles.btnAcao} ${styles.btnAcaoDel}`} onClick={onExcluir} title="Excluir">🗑</button>
      </td>
    </tr>
  );
}
